import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-link-expiration',
  templateUrl: './link-expiration.component.html',
  styleUrls: ['./link-expiration.component.scss']
})
export class LinkExpirationComponent implements OnInit, OnDestroy {
  @Input() expirationDate: Date | null = null;
  @Output() expirationStatusChanged = new EventEmitter<boolean>();
  
  remainingTime: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } = { days: 0, hours: 0, minutes: 0, seconds: 0 };
  
  timePercentage: number = 100;
  private timerSubscription: Subscription | null = null;
  linkExpired: boolean = false;
  isCriticalTime: boolean = false; // Menos de 5 minutos
  isWarningTime: boolean = false; // Menos de 10 minutos

  ngOnInit(): void {
    if (this.expirationDate) {
      this.startExpirationTimer();
    }
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  startExpirationTimer(): void {
    const now = new Date();
    if (this.expirationDate && this.expirationDate < now) {
      this.linkExpired = true;
      this.timePercentage = 0;
      this.remainingTime = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      this.emitExpirationStatus(true);
      return;
    }

    // Actualizar inmediatamente
    this.updateRemainingTime();

    // Actualizar cada segundo
    this.timerSubscription = interval(1000).subscribe(() => {
      this.updateRemainingTime();
    });
  }

  updateRemainingTime(): void {
    if (!this.expirationDate) return;
  
    const now = new Date();
    if (this.expirationDate <= now) {
      this.linkExpired = true;
      this.timePercentage = 0;
      this.remainingTime = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      this.emitExpirationStatus(true);
      if (this.timerSubscription) {
        this.timerSubscription.unsubscribe();
      }
      return;
    }
  
    const totalMs = this.expirationDate.getTime() - now.getTime();
  
    // Calcular porcentaje de tiempo restante (asumiendo validez de 3 horas)
    const totalValidityPeriod = 3 * 60 * 60 * 1000; // 3 horas en milisegundos
    this.timePercentage = Math.min(
      100,
      Math.max(0, (totalMs / totalValidityPeriod) * 100)
    );
  
    // Calcular tiempo restante en formato legible
    const totalSeconds = Math.floor(totalMs / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;
  
    this.remainingTime = { days, hours, minutes, seconds };
    
    // Verificar tiempo crítico (menos de 5 minutos)
    this.isCriticalTime = totalMs < 5 * 60 * 1000;
    
    // Verificar tiempo de advertencia (menos de 10 minutos)
    this.isWarningTime = totalMs < 10 * 60 * 1000 && totalMs >= 5 * 60 * 1000;
    
    // Emitir estado actual de expiración
    this.emitExpirationStatus(false);
  }

  private emitExpirationStatus(expired: boolean): void {
    if (this.linkExpired !== expired) {
      this.linkExpired = expired;
      this.expirationStatusChanged.emit(expired);
    }
  }

  /**
   * Obtiene la clase de estilo para el progress bar según el tiempo restante
   */
  getProgressBarClass(): string {
    if (this.isCriticalTime) {
      return 'progress-critical';
    } else if (this.isWarningTime) {
      return 'progress-warning';
    }
    return 'progress-normal';
  }
}